import { idToken } from '../firebase';
import type { Deal, PublicPost, Work } from '../data/types';
import { loadProfile } from './state';

/** Empty in production: the API rides Firebase Hosting's rewrite on the app's own origin. */
export const BACKEND = ((import.meta.env.VITE_BACKEND_URL as string | undefined) || '').replace(/\/$/, '');

export interface CreatePostInput {
  challengeId: string;
  deal: Deal;
  work: Work;
  day: number;
  durationSec: number;
  endedEarly: boolean;
  headline: string;
  invite: boolean;
  card: Blob;
  /** Day 1's talk, kept for the person, private; or, when it was kept as it ended, the story it became */
  warmup?: { blob: Blob | null; seconds: number; storyId?: string } | null;
}

export interface CreatedPost { postId: string; cardUrl: string; shareUrl: string; audioUrl?: string }

/**
 * The card PNG and the post go through the backend (routes/kindling.ts), which
 * stores the image publicly so the link preview can use it, and writes the
 * post document. The photo data URL never leaves the device: it is baked into
 * the PNG and stripped from `work` here. Only the fact of it travels, as
 * `hasPhoto`, so the post can say it was a drawing (#114). The one exception is
 * "Who are you?", whose pinned pictures go as files for the crew to draw a
 * character from (below); they too are stripped from `work`.
 */
export async function createPost(input: CreatePostInput): Promise<CreatedPost> {
  const token = await idToken();
  const { photo, audio, made, selfie, ...rest } = input.work;
  // "Who are you?" can carry several photographs and a photo of the person; like the one above, they stay out of `work`
  const work = {
    ...rest,
    ...(made ? { made: made.map(({ photo: p, ...m }) => ({ ...m, hasPhoto: !!p })) } : {}),
    ...(selfie ? { hasSelfie: true } : {}),
  };
  const meta = { ...input, work, hasPhoto: !!photo, card: undefined, warmup: undefined, warmupSec: input.warmup?.seconds, audioSec: work.audioSec, storyId: input.warmup?.storyId };
  const form = new FormData();
  form.append('meta', JSON.stringify(meta));
  form.append('card', input.card, 'card.png');
  if (!input.warmup?.storyId && input.warmup?.blob && input.warmup.blob.size > 0) form.append('warmup', input.warmup.blob, 'warmup.webm');
  if (audio && audio.size > 0) form.append('audio', audio, 'story.webm');
  // "Who are you?": the crew draw your character from the pictures of your work
  // (backend kindling-render.service), so those go too — kept privately, and
  // never at all when the crew are off.
  if (made && loadProfile().crew !== 'off') {
    for (const m of made.filter((x) => x.photo?.startsWith('data:image/')).slice(0, MAX_MADE_REFS)) form.append('made', dataUrlBlob(m.photo!), `${m.id}.jpg`);
  }
  // …and the photo of them, so the character looks like them
  if (selfie?.startsWith('data:image/') && loadProfile().crew !== 'off') form.append('selfie', dataUrlBlob(selfie), 'me.jpg');
  const res = await fetch(`${BACKEND}/api/kindling/posts`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form });
  if (!res.ok) throw new Error(await explain(res));
  return res.json();
}

/** How many pinned pictures the crew draw a character from (backend MAX_MADE_REFS). */
export const MAX_MADE_REFS = 6;

function dataUrlBlob(dataUrl: string): Blob {
  const [head, b64] = dataUrl.split(',');
  const type = /data:([^;]+)/.exec(head)?.[1] || 'image/jpeg';
  const bin = atob(b64 || '');
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

/** A readable reason for a failed post, so the toast (and the dev console) says what actually went wrong. */
async function explain(res: Response): Promise<string> {
  const html = (res.headers.get('content-type') || '').includes('text/html');
  // The dev proxy answers with the app's index.html when it is not routing /api/kindling to the backend
  // (a Vite server started before vite.config.ts gained the proxy, or a backend that is not running).
  if (html) return `post failed: ${res.status} (the backend is not reachable through the dev proxy; restart the Kindling dev server and check the backend on 41703)`;
  let detail = '';
  try { detail = JSON.stringify(await res.json()).slice(0, 200); } catch { /* no body */ }
  return `post failed: ${res.status}${detail ? ` ${detail}` : ''}`;
}

export async function fetchPost(postId: string): Promise<PublicPost | null> {
  const res = await fetch(`${BACKEND}/api/kindling/posts/${encodeURIComponent(postId)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`fetch post failed: ${res.status}`);
  return res.json();
}
